/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useId} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Text, Stack, HStack} from '@chakra-ui/react'
import {StatusOffIcon, StatusOnIcon} from '../icons'
import {validatePassword} from '../../utils/password-utils'

/**
 * UI component for styling password requirement line
 */
const PasswordRequirement = ({isValid, children}) => {
    const requirementID = useId()
    const {formatMessage} = useIntl()
    const iconStyles = {
        display: 'block',
        boxSize: 4,
        color: 'stokkeCore.black',
        role: 'img'
    }

    const fulfilled = formatMessage({
        id: 'password_requirements.assistive_msg.fulfilled',
        defaultMessage: 'fulfilled'
    })

    const notFulfilled = formatMessage({
        id: 'password_requirements.assistive_msg.not_fulfilled',
        defaultMessage: 'not fulfilled'
    })

    return (
        <HStack as="li" align="flex-start" spacing={2.5}>
            {isValid ? (
                <StatusOnIcon
                    {...iconStyles}
                    aria-label={fulfilled}
                    aria-labelledby={requirementID}
                />
            ) : (
                <StatusOffIcon
                    {...iconStyles}
                    aria-label={notFulfilled}
                    aria-labelledby={requirementID}
                />
            )}
            <Text textStyle="Regular Small" id={requirementID}>
                {children}
            </Text>
        </HStack>
    )
}

PasswordRequirement.propTypes = {
    /** Should it render in valid state */
    isValid: PropTypes.bool,

    /** The requirement text */
    children: PropTypes.any,

    /** Element ID */
    id: PropTypes.string
}

/**
 * Renders a list of password requirments. Each requirement line toggles to its `isValid`
 * state when the given password value meets the associated critieria.
 */
const PasswordRequirements = ({value}) => {
    const pwValidations = validatePassword(value)

    return (
        <Stack as="ul" spacing={2}>
            <PasswordRequirement isValid={pwValidations.hasMinChars}>
                <FormattedMessage
                    id="password_requirements.error.eight_letter_minimum"
                    defaultMessage="8 characters minimum"
                    description="Password requirement"
                />
            </PasswordRequirement>
            <PasswordRequirement isValid={pwValidations.hasUppercase}>
                <FormattedMessage
                    id="password_requirements.error.one_uppercase_letter"
                    defaultMessage="1 uppercase letter"
                    description="Password requirement"
                />
            </PasswordRequirement>
            <PasswordRequirement isValid={pwValidations.hasLowercase}>
                <FormattedMessage
                    id="password_requirements.error.one_lowercase_letter"
                    defaultMessage="1 lowercase letter"
                    description="Password requirement"
                />
            </PasswordRequirement>
            <PasswordRequirement isValid={pwValidations.hasNumber}>
                <FormattedMessage
                    defaultMessage="1 number"
                    description="Password requirement"
                    id="password_requirements.error.one_number"
                />
            </PasswordRequirement>
            <PasswordRequirement isValid={pwValidations.hasSpecialChar}>
                <FormattedMessage
                    id="password_requirements.error.one_special_character"
                    defaultMessage="1 special character (example: , S ! % #)"
                    description="Password requirement"
                />
            </PasswordRequirement>
        </Stack>
    )
}

PasswordRequirements.propTypes = {
    /** The password to check against */
    value: PropTypes.string
}

export default PasswordRequirements
